import {css, useTheme} from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import React, {
  KeyboardEventHandler,
  MouseEventHandler,
  useCallback,
} from 'react';
import {animated, Spring} from 'react-spring';

import hierarchyArrowIcon from 'src/assets/icons/hierarchyArrowIcon.svg';
import hierarchyFileIcon from 'src/assets/icons/hierarchyFileIcon.svg';
import {GetStylesFunction} from 'src/js/common/types';
import {HierarchyTreeNode as HierarchyTreeNodeModel} from 'src/js/model/hierarchy_tree_children';

interface OwnProps {
  isOpened?: boolean;
  node: HierarchyTreeNodeModel;
  tabIndex?: number;
  toggleOpened?: (node: HierarchyTreeNodeModel) => void;
}

type Props = OwnProps;

export const HierarchyNodeTick: React.FC<Props> = (props) => {
  const {node, isOpened = false, tabIndex = 0, toggleOpened} = props;

  const nodeHasChildren = node.hasChildren();
  const theme = useTheme();
  const styles = getStyles({theme, params: {nodeHasChildren}});

  const handleTickClick: MouseEventHandler<HTMLElement> = useCallback(
    (event) => {
      event.stopPropagation();
      if (toggleOpened) {
        toggleOpened(node);
      }
    },
    [node, toggleOpened]
  );

  const handleTickKeyDown: KeyboardEventHandler<HTMLElement> = useCallback(
    (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.stopPropagation();
        if (toggleOpened) {
          toggleOpened(node);
        }
      }
      return true;
    },
    [node, toggleOpened]
  );

  return (
    <Spring
      from={{transform: 'rotate(0deg)'}}
      to={{transform: `rotate(${isOpened ? 90 : 0}deg)`}}
    >
      {({transform}) => (
        <animated.div
          data-name={'hierarchy-node-tick--animated-div-icon-wrapper'}
          onClick={handleTickClick}
          onKeyDown={handleTickKeyDown}
          tabIndex={tabIndex}
          css={
            nodeHasChildren
              ? styles.dlHierarchyNodeTick
              : styles.hierarchyFileIcon
          }
          style={{transform}}
        >
          <SvgIcon
            inheritViewBox
            css={styles.icon}
            component={nodeHasChildren ? hierarchyArrowIcon : hierarchyFileIcon}
          />
        </animated.div>
      )}
    </Spring>
  );
};

HierarchyNodeTick.displayName = 'HierarchyNodeTick';

const getStyles: GetStylesFunction = (options) => ({
  hierarchyFileIcon: css`
    cursor: default;
    width: 16px;
    height: 16px;

    color: var(--dlHierarchyNodeTickColor);
  `,
  dlHierarchyNodeTick: css`
    cursor: pointer;
    width: 16px;
    height: 16px;

    color: var(--dlHierarchyNodeTickColor);

    &:active,
    &:visited {
      color: var(--dlHierarchyNodeTickColor);
    }

    &:hover,
    &:focus,
    &:active:focus,
    &:active:hover {
      color: var(--dlHierarchyNodeTickColorFocus);
      outline: none;
    }
  `,
  icon: css`
    width: 16px;
    height: 16px;
    vertical-align: unset;
  `,
});
